import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import Layout from "../../components/layout";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import {
  Form, Input, Button, DatePicker, Modal, Table, Typography, Avatar
} from "antd";
import {
  SearchOutlined, CheckCircleOutlined, CalendarOutlined,
  UserOutlined, MedicineBoxOutlined,
  ClockCircleOutlined, ClearOutlined
} from "@ant-design/icons";
import moment from "moment";
import toast from "react-hot-toast";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("complete-appt-styles")) return;
  const el = document.createElement("style");
  el.id = "complete-appt-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .ca-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .ca-wrap { max-width: 1100px; margin: 0 auto; padding: 32px 24px 64px; }

    /* Hero */
    .ca-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 28px;
    }
    .ca-hero::before {
      content:''; position:absolute; top:-80px; right:-80px;
      width:300px; height:300px; border-radius:50%;
      background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);
      filter:blur(32px); pointer-events:none;
    }
    .ca-hero::after {
      content:''; position:absolute; bottom:-60px; left:-40px;
      width:200px; height:200px; border-radius:50%;
      background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);
      filter:blur(26px); pointer-events:none;
    }
    .ca-eyebrow {
      display:inline-block; padding:4px 12px; border-radius:20px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
      font-family:'Sora',sans-serif; margin-bottom:12px;
    }
    .ca-hero-badge {
      background:rgba(255,255,255,0.12); border-radius:20px;
      padding:4px 12px; font-size:12px; color:rgba(255,255,255,0.75);
      font-family:'DM Sans',sans-serif; display:inline-block; margin-top:12px;
    }

    /* Toolbar */
    .ca-toolbar {
      background:${WHITE}; border-radius:16px;
      box-shadow:0 4px 20px rgba(0,0,0,.06);
      padding:16px 20px; margin-bottom:20px;
      display:flex; align-items:center; gap:12px; flex-wrap:wrap;
    }
    .ca-root .ca-search.ant-input-affix-wrapper {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important; flex:1; min-width:200px;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .ca-root .ca-search.ant-input-affix-wrapper:hover,
    .ca-root .ca-search.ant-input-affix-wrapper-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .ca-root .ca-search .ant-input { font-family:'DM Sans',sans-serif !important; font-size:13px !important; }
    .ca-btn-outline {
      height:40px !important; padding:0 18px !important; border-radius:10px !important;
      font-size:13px !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; background:${WHITE} !important;
      color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important;
    }
    .ca-btn-outline:hover { border-color:${TEAL} !important; color:${TEAL} !important; }

    /* Table */
    .ca-table-wrap {
      background:${WHITE}; border-radius:20px;
      box-shadow:0 4px 20px rgba(0,0,0,.06); overflow:hidden;
    }
    .ca-root .ant-table-thead > tr > th {
      background:${SURFACE} !important; color:${SLATE_MID} !important;
      font-size:11px !important; font-weight:700 !important;
      letter-spacing:.5px; text-transform:uppercase;
      border-bottom:1.5px solid #f1f5f9 !important;
      font-family:'Sora',sans-serif !important; padding:14px 16px !important;
    }
    .ca-root .ant-table-tbody > tr > td {
      font-size:13px !important; font-family:'DM Sans',sans-serif !important;
      color:${SLATE_MID} !important; padding:14px 16px !important;
      border-bottom:1px solid #f8fafc !important;
    }
    .ca-root .ant-table-tbody > tr:hover > td { background:#f0fdfb !important; }
    .ca-root .ant-pagination { padding:16px !important; }
    .ca-root .ant-pagination-item-active { border-color:${TEAL} !important; }
    .ca-root .ant-pagination-item-active a { color:${TEAL} !important; }

    /* Status badge */
    .ca-badge {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
      display:inline-block; background:${TEAL_LIGHT}; color:${EMERALD};
    }

    /* Complete button */
    .ca-complete-btn {
      height:30px !important; padding:0 14px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important;
      font-family:'Sora',sans-serif !important; border:none !important;
      background:linear-gradient(135deg,${EMERALD},${TEAL}) !important;
      color:${WHITE} !important; box-shadow:0 2px 8px ${TEAL}44 !important;
      transition:transform .2s,box-shadow .2s !important;
    }
    .ca-complete-btn:hover {
      transform:translateY(-1px) !important; box-shadow:0 6px 16px ${TEAL}55 !important;
    }

    /* ── Modal ── */
    .ca-modal .ant-modal-content { border-radius:20px !important; overflow:hidden; padding:0 !important; }
    .ca-modal .ant-modal-header {
      background:linear-gradient(135deg,${SLATE},${SLATE_MID}) !important;
      padding:20px 28px !important; border-radius:0 !important; margin:0 !important;
    }
    .ca-modal .ant-modal-title {
      color:${WHITE} !important; font-family:'Sora',sans-serif !important;
      font-weight:700 !important; font-size:16px !important;
    }
    .ca-modal .ant-modal-close { color:${MUTED} !important; top:18px !important; }
    .ca-modal .ant-modal-close:hover { color:${WHITE} !important; }
    .ca-modal .ant-modal-body { padding:24px 28px !important; }
    .ca-modal .ant-modal-footer { padding:16px 28px !important; border-top:1.5px solid #f1f5f9 !important; }

    /* Appointment info strip */
    .ca-appt-strip {
      background:${SURFACE}; border:1.5px solid #f1f5f9;
      border-radius:14px; padding:16px 18px; margin-bottom:24px;
      display:flex; flex-direction:column; gap:10px;
    }
    .ca-strip-row { display:flex; align-items:center; gap:10px; }
    .ca-strip-icon {
      width:28px; height:28px; border-radius:8px; flex-shrink:0;
      display:flex; align-items:center; justify-content:center; font-size:13px;
    }

    /* Modal form */
    .ca-modal .ant-form-item { margin-bottom:16px !important; }
    .ca-modal .ant-form-item-label { padding-bottom:4px !important; }
    .ca-modal .ant-form-item-label > label {
      font-size:12px !important; font-weight:600 !important;
      color:${SLATE_MID} !important; height:20px !important;
    }
    .ca-modal .ant-input,
    .ca-modal .ant-input-affix-wrapper {
      border-radius:10px !important;
      border:1.5px solid #e2e8f0 !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s !important;
    }
    .ca-modal textarea.ant-input { padding:10px 12px !important; height:auto !important; line-height:1.6 !important; }
    .ca-modal .ant-input:hover, .ca-modal .ant-input:focus {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .ca-modal .ant-picker {
      border-radius:10px !important; height:44px !important;
      border:1.5px solid #e2e8f0 !important; width:100% !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s !important;
    }
    .ca-modal .ant-picker:hover, .ca-modal .ant-picker-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    /* Follow-up radio */
    .ca-radio-group { display:flex; gap:10px; }
    .ca-radio-card {
      flex:1; border:1.5px solid #e2e8f0; border-radius:10px;
      padding:10px 14px; cursor:pointer; text-align:center;
      transition:border-color .2s,background .2s;
      font-size:13px; font-weight:600; color:${SLATE_MID};
      font-family:'DM Sans',sans-serif;
    }
    .ca-radio-card:hover { border-color:${TEAL}; background:#f0fdfb; }
    .ca-radio-card.selected-yes { border-color:${TEAL}; background:${TEAL_LIGHT}; color:${EMERALD}; }
    .ca-radio-card.selected-no  { border-color:#6366f1; background:#eef2ff; color:#6366f1; }

    /* Modal footer buttons */
    .ca-modal-cancel {
      height:40px !important; padding:0 20px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:600 !important;
      font-family:'DM Sans',sans-serif !important;
      border:1.5px solid #e2e8f0 !important; color:${SLATE_MID} !important;
    }
    .ca-modal-submit {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important;
      font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,${EMERALD},${TEAL}) !important;
      border:none !important; color:${WHITE} !important;
      box-shadow:0 4px 12px ${TEAL}44 !important;
    }

    /* Animations */
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .ca-fade   { animation:fadeUp .5s ease both; }
    .ca-fade-1 { animation-delay:.05s; }
    .ca-fade-2 { animation-delay:.12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ──────────────────────────────────────────── */
function AdminCompleteAppointment() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [appointments,         setAppointments]         = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [selectedAppointment,  setSelectedAppointment]  = useState(null);
  const [isModalVisible,       setIsModalVisible]       = useState(false);
  const [searchText,           setSearchText]           = useState("");
  const [followUpNeeded,       setFollowUpNeeded]       = useState(false);

  useEffect(() => { injectStyles(); }, []);

  const fetchAppointments = useCallback(async () => {
    try {
      dispatch(showLoading());
      const response = await axios.get("/api/admin/get-all-appointments", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      const approved = response.data.data.filter(a => a.status === "approved");
      setAppointments(approved);
      setFilteredAppointments(approved);
    } catch {
      toast.error("Failed to fetch appointments");
      dispatch(hideLoading());
    }
  }, [dispatch]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const handleSearch = (value) => {
    setSearchText(value);
    if (value) {
      setFilteredAppointments(appointments.filter(a =>
        a.userInfo.name.toLowerCase().includes(value.toLowerCase()) ||
        a.doctorInfo.service.toLowerCase().includes(value.toLowerCase())
      ));
    } else {
      setFilteredAppointments(appointments);
    }
  };

  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedAppointment(null);
    setFollowUpNeeded(false);
    form.resetFields();
  };

  const completeAppointment = async (values) => {
    try {
      dispatch(showLoading());
      const response = await axios.post(
        "/api/admin/complete-appointment",
        { ...values, appointmentId: selectedAppointment._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success(response.data.message);
        handleModalCancel();
        fetchAppointments();
      }
    } catch {
      toast.error("Something went wrong");
      dispatch(hideLoading());
    }
  };

  const handleFollowUpChange = (val) => {
    setFollowUpNeeded(val === "yes");
    if (val === "no") form.setFieldsValue({ followUpDate: null });
    form.setFieldsValue({ followUpNeeded: val });
  };

  /* ── Columns ── */
  const columns = [
    {
      title: "Patient",
      dataIndex: ["userInfo", "name"],
      key: "name",
      render: (name) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={32} style={{ background: `linear-gradient(135deg,${EMERALD},${TEAL})`, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {name?.[0]?.toUpperCase()}
          </Avatar>
          <Text strong style={{ fontSize: 13, color: SLATE }}>{name}</Text>
        </div>
      ),
    },
    {
      title: "Service",
      dataIndex: ["doctorInfo", "service"],
      key: "service",
      render: text => <Text style={{ fontSize: 13, color: SLATE_MID }}>{text}</Text>,
    },
    {
      title: "Doctor",
      key: "doctor",
      render: (_, record) => (
        <Text style={{ fontSize: 13, color: SLATE_MID }}>
          Dr. {record.doctorInfo?.firstName} {record.doctorInfo?.lastName}
        </Text>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: date => <Text style={{ fontSize: 13, color: SLATE_MID }}>{moment(date).format("DD/MM/YYYY")}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: () => <span className="ca-badge">Approved</span>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button className="ca-complete-btn" icon={<CheckCircleOutlined />}
          onClick={() => handleAppointmentSelect(record)}>
          Complete
        </Button>
      ),
    },
  ];

  return (
    <Layout>
      <div className="ca-root">
        <div className="ca-wrap">

          {/* Hero */}
          <div className="ca-hero ca-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="ca-eyebrow"><CheckCircleOutlined style={{ marginRight: 5 }} />Appointments</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Complete Appointments
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Review and mark approved appointments as completed with notes and follow-up details
              </Text>
              <div>
                <div className="ca-hero-badge">
                  <CalendarOutlined style={{ marginRight: 5 }} />
                  {filteredAppointments.length} Pending Completion
                </div>
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="ca-toolbar ca-fade ca-fade-1">
            <Input
              className="ca-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search by patient name or service..."
              value={searchText}
              onChange={e => handleSearch(e.target.value)}
              allowClear
            />
            <Button className="ca-btn-outline" icon={<ClearOutlined />}
              onClick={() => { setSearchText(""); setFilteredAppointments(appointments); }}>
              Clear
            </Button>
          </div>

          {/* Table */}
          <div className="ca-table-wrap ca-fade ca-fade-2">
            <Table
              columns={columns}
              dataSource={filteredAppointments}
              rowKey="_id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 800 }}
            />
          </div>

          {/* Complete Modal */}
          <Modal
            className="ca-modal"
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: TEAL_LIGHT, color: EMERALD, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CheckCircleOutlined />
                </div>
                <span>Complete Appointment</span>
              </div>
            }
            open={isModalVisible}
            onCancel={handleModalCancel}
            footer={null}
            width={580}
            destroyOnClose
          >
            {/* Appointment info strip */}
            {selectedAppointment && (
              <div className="ca-appt-strip">
                <div className="ca-strip-row">
                  <div className="ca-strip-icon" style={{ background: TEAL_LIGHT, color: EMERALD }}>
                    <UserOutlined />
                  </div>
                  <div>
                    <Text style={{ fontSize: 11, color: MUTED, display: "block" }}>Patient</Text>
                    <Text strong style={{ fontSize: 13, color: SLATE }}>{selectedAppointment.userInfo.name}</Text>
                  </div>
                </div>
                <div className="ca-strip-row">
                  <div className="ca-strip-icon" style={{ background: "#eff6ff", color: "#3b82f6" }}>
                    <MedicineBoxOutlined />
                  </div>
                  <div>
                    <Text style={{ fontSize: 11, color: MUTED, display: "block" }}>Service</Text>
                    <Text strong style={{ fontSize: 13, color: SLATE }}>{selectedAppointment.doctorInfo.service}</Text>
                  </div>
                </div>
                <div className="ca-strip-row">
                  <div className="ca-strip-icon" style={{ background: "#faf5ff", color: "#8b5cf6" }}>
                    <ClockCircleOutlined />
                  </div>
                  <div>
                    <Text style={{ fontSize: 11, color: MUTED, display: "block" }}>Appointment Date</Text>
                    <Text strong style={{ fontSize: 13, color: SLATE }}>
                      {moment(selectedAppointment.date).format("DD/MM/YYYY HH:mm")}
                    </Text>
                  </div>
                </div>
              </div>
            )}

            <Form form={form} layout="vertical" onFinish={completeAppointment} requiredMark={false}>

              <Form.Item label="Comments & Notes" name="comment"
                rules={[{ required: true, message: "Please add comments about the appointment" }]}>
                <Input.TextArea
                  rows={4}
                  placeholder="Enter details about the appointment completion, findings, recommendations..."
                />
              </Form.Item>

              <Form.Item label="Is Follow-Up Needed?" name="followUpNeeded"
                rules={[{ required: true, message: "Please select an option" }]}>
                <div className="ca-radio-group">
                  {["yes", "no"].map(val => (
                    <div
                      key={val}
                      className={`ca-radio-card ${followUpNeeded && val === "yes" ? "selected-yes" : !followUpNeeded && val === "no" && form.getFieldValue("followUpNeeded") === "no" ? "selected-no" : ""}`}
                      onClick={() => handleFollowUpChange(val)}
                    >
                      {val === "yes" ? "✓ Yes, Schedule Follow-Up" : "✗ No Follow-Up Needed"}
                    </div>
                  ))}
                </div>
              </Form.Item>

              {followUpNeeded && (
                <Form.Item label="Follow-Up Date" name="followUpDate"
                  rules={[{ required: true, message: "Please select a follow-up date" }]}>
                  <DatePicker
                    placeholder="Select follow-up date"
                    suffixIcon={<CalendarOutlined style={{ color: MUTED }} />}
                    disabledDate={current => current && current < moment().endOf("day")}
                  />
                </Form.Item>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                <Button className="ca-modal-cancel" onClick={handleModalCancel}>Cancel</Button>
                <Button className="ca-modal-submit" htmlType="submit" icon={<CheckCircleOutlined />}>
                  Complete Appointment
                </Button>
              </div>

            </Form>
          </Modal>

        </div>
      </div>
    </Layout>
  );
}

export default AdminCompleteAppointment;